import {
	CheckCircle2,
	Database,
	Loader2,
	RotateCcw,
	ShieldAlert,
	Wand2,
	X,
	XCircle,
} from "lucide-react";
import { Button } from "@/components/atoms/studio/ui/button";
import type {
	OrgConfigApplyResult,
	OrgConfigProposal,
	OrgConfigVerdictResult,
} from "@/services/testing";

/**
 * Consent card for the org-config loop. When a run surfaces a PROVEN org-config gap (a field off the page
 * layout, a permission not granted, a quick action missing) — whether the run was BLOCKED by it, or only
 * PASSED because the engine wrote around it via the API fallback (a degraded pass) — and the fix maps to a
 * safe, additive, reversible change, the user can consent to apply it via the Salesforce API and re-run,
 * turning the previously advisory "what to do" text into a one-click, verified fix. Mirrors HealProposalCard's
 * shape; the difference is this changes the ORG (sandbox-only, reversible), not the test.
 */

const CHANGE_LABEL: Record<OrgConfigProposal["changeType"], string> = {
	add_field_to_layout: "Add field to layout",
	grant_field_permission: "Grant field access",
	grant_object_permission: "Grant object access",
	add_quick_action_to_layout: "Add quick action",
};

const VERDICT_META: Record<
	OrgConfigVerdictResult["verdict"],
	{ label: string; className: string; icon: typeof CheckCircle2 }
> = {
	correct: {
		label: "Diagnosis confirmed",
		className: "border-success/30 bg-success/5 text-success-strong",
		icon: CheckCircle2,
	},
	incorrect: {
		label: "Diagnosis was wrong",
		className: "border-destructive/30 bg-destructive/5 text-destructive",
		icon: XCircle,
	},
	inconclusive: {
		label: "Inconclusive re-run",
		className: "border-border/50 bg-card/60 text-muted-foreground",
		icon: ShieldAlert,
	},
};

export function OrgConfigProposalCard({
	proposal,
	deciding,
	applyResult,
	verdict,
	onAccept,
	onReject,
	onRevert,
}: {
	proposal: OrgConfigProposal;
	deciding?: boolean;
	applyResult?: OrgConfigApplyResult | null;
	verdict?: OrgConfigVerdictResult | null;
	onAccept: () => void;
	onReject: () => void;
	onRevert: () => void;
}) {
	const applied = !!applyResult?.applied;
	const failed = !!applyResult && !applyResult.applied;

	return (
		<div className="space-y-2 rounded-md border border-warning/40 bg-warning/5 p-2.5 text-xs">
			<div className="flex flex-wrap items-center gap-2">
				<Database className="h-3.5 w-3.5 shrink-0 text-warning-strong" />
				<span className="font-semibold text-warning-strong">Apply org configuration</span>
				<span className="rounded border border-border/70 bg-card/60 px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
					{CHANGE_LABEL[proposal.changeType]}
				</span>
				<span className="ml-auto font-mono text-[11px] text-muted-foreground">
					{proposal.sobject}
					{proposal.field ? `.${proposal.field}` : proposal.quickAction ? ` · ${proposal.quickAction}` : ""}
				</span>
			</div>

			<p className="leading-relaxed text-foreground/90">{proposal.humanSummary}</p>

			{/* This writes to the org (sandbox-only, additive, reversible) — say so plainly before consent. */}
			<div className="flex items-center gap-1.5 rounded border border-border/50 bg-card/50 px-2 py-1 text-[11px] text-muted-foreground">
				<ShieldAlert className="h-3.5 w-3.5 shrink-0" />
				<span>
					Applies to the connected <strong>sandbox</strong> via the Salesforce API, attributed to your
					user. Additive and reversible.
				</span>
			</div>

			{applied && (
				<div className="flex items-center gap-1.5 rounded border border-success/30 bg-success/5 px-2 py-1 text-[11px] text-success-strong">
					<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
					<span>{applyResult?.message ?? "Applied — re-running to verify…"}</span>
				</div>
			)}
			{failed && (
				<div className="flex items-center gap-1.5 rounded border border-destructive/30 bg-destructive/5 px-2 py-1 text-[11px] text-destructive">
					<XCircle className="h-3.5 w-3.5 shrink-0" />
					<span>{applyResult?.message ?? "Could not apply the change."}</span>
				</div>
			)}
			{verdict && (
				<VerdictRow verdict={verdict} />
			)}

			<div className="flex flex-wrap items-center gap-1.5 border-t border-border/40 pt-2">
				{!applied ? (
					<>
						<Button
							size="sm"
							className="h-7 text-xs"
							onClick={onAccept}
							disabled={deciding}
							title="Apply this change to the sandbox and re-run the test to verify"
						>
							{deciding ? (
								<Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
							) : (
								<Wand2 className="mr-1 h-3.5 w-3.5" />
							)}
							Apply &amp; re-run
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="h-7 text-xs"
							onClick={onReject}
							disabled={deciding}
							title="Dismiss — the org is left untouched"
						>
							<X className="mr-1 h-3.5 w-3.5" />
							Dismiss
						</Button>
						<span className="ml-auto text-[11px] text-muted-foreground">
							The org is unchanged until you apply.
						</span>
					</>
				) : (
					<Button
						size="sm"
						variant="outline"
						className="h-7 text-xs"
						onClick={onRevert}
						disabled={deciding}
						title="Undo the org-config change"
					>
						{deciding ? (
							<Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
						) : (
							<RotateCcw className="mr-1 h-3.5 w-3.5" />
						)}
						Undo change
					</Button>
				)}
			</div>
		</div>
	);
}

function VerdictRow({ verdict }: { verdict: OrgConfigVerdictResult }) {
	const meta = VERDICT_META[verdict.verdict];
	const Icon = meta.icon;
	return (
		<div className={`flex items-start gap-1.5 rounded border px-2 py-1 text-[11px] ${meta.className}`}>
			<Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
			<span>
				<strong>{meta.label}.</strong> {verdict.reason}
			</span>
		</div>
	);
}
